import { Moment } from 'moment';
import {
	ServiceScheduleItemCaseType,
	ServiceScheduleItemMember,
	ServiceScheduleType,
	useLazyWorkplaceServiceScheduleQuery,
	WorkplaceMemberRole,
} from '../../../graphql/graphql';
import {
	ServiceScheduleEvent,
	ServiceScheduleEventCaseType,
	ServiceScheduleEventWorker,
} from '../Interfaces/ServiceScheduleEvent.interface';
import { useState } from 'react';
import useTranslation from '../../../Hooks/useTranslation';
import { getDateTimeWithDst, getMomentDate } from '../../../Utils/dates.util';
import moment from 'moment';
export default function useServiceSchedules() {
	const { t } = useTranslation();
	const [getServiceSchedules] = useLazyWorkplaceServiceScheduleQuery();
	const [isLoading, setIsLoading] = useState(false);

	const EVENT_PRESET: Record<
		ServiceScheduleType,
		{ label: string; eventColor: string }
	> = {
		[ServiceScheduleType.Shift]: {
			label: 'base.shift',
			eventColor: '#b6d3fc',
		},
		[ServiceScheduleType.ShiftExtra]: {
			label: 'base.extraService',
			eventColor: '#62a2ff',
		},
		[ServiceScheduleType.OnlineOrder]: {
			label: 'component.workplaceOrderingSettingsCalendar.eventTitle',
			eventColor: '#62a2ff',
		},
		[ServiceScheduleType.OnlineConnect]: {
			label: '',
			eventColor: '',
		},
		[ServiceScheduleType.Default]: {
			label: '',
			eventColor: '',
		},
	};

	const prepareDateTime = (
		originDate: string,
		originTime: string,
		date: string,
		time: string,
	): Moment => {
		return getDateTimeWithDst(originDate, date, time, originTime);
	};

	const prepareCaseTypes = (
		serviceScheduleItemCaseTypes: ServiceScheduleItemCaseType[],
	): ServiceScheduleEventCaseType[] => {
		const caseTypes: ServiceScheduleEventCaseType[] = [];

		serviceScheduleItemCaseTypes?.forEach(serviceScheduleItemCaseType => {
			const value = serviceScheduleItemCaseType.workplaceCaseType?.id;
			const label =
				serviceScheduleItemCaseType.workplaceCaseType?.caseType?.label;
			if (value && label) {
				caseTypes.push({
					value,
					label,
					eventInterval:
						serviceScheduleItemCaseType.workplaceCaseType?.caseType
							?.eventInterval ?? undefined,
				});
			}
		});

		return caseTypes;
	};

	const prepareWorkers = (
		serviceScheduleItemMembers: ServiceScheduleItemMember[],
	): ServiceScheduleEventWorker[] => {
		let workers: ServiceScheduleEventWorker[] = [];

		serviceScheduleItemMembers?.forEach(serviceScheduleItemMember => {
			const name =
				serviceScheduleItemMember.workplaceMember?.user?.profile?.fullName;
			const userId = serviceScheduleItemMember.workplaceMember?.user?.id;
			const workplaceMemberId = serviceScheduleItemMember.workplaceMemberId;
			const roles = (serviceScheduleItemMember.workplaceMember
				?.workplaceMemberRoles || []) as WorkplaceMemberRole[];
			if (name && userId && workplaceMemberId) {
				workers.push({
					role: roles,
					name,
					userId,
					workplaceMemberId,
					isDoctor: roles.some(({ role }) => role?.key === 'doctor') ?? false,
				});
			}
		});

		return workers.sort((a, b) => (a.isDoctor ? -1 : 1));
	};

	const getWorkplaceSchedules = async ({
		from,
		to,
		allDay,
		workplaceIds,
		type,
	}: {
		from: Moment;
		to: Moment;
		allDay?: boolean;
		workplaceIds?: string[];
		type: ServiceScheduleType;
	}) => {
		setIsLoading(true);
		const tempEvents: ServiceScheduleEvent[] = [];

		const data = await getServiceSchedules({
			from: from.format(),
			to: to.format(),
			workplaceIds,
			type,
			includeOccurrences: true,
		});

		data?.data?.serviceSchedules?.data?.forEach(serviceSchedule => {
			serviceSchedule?.serviceScheduleItems?.forEach(serviceScheduleItem => {
				if (serviceScheduleItem?.id) {
					const baseEventData = {
						id: serviceScheduleItem.id,
						scheduleId: serviceSchedule.id,
						scheduleItemId: serviceScheduleItem.id,
						serviceScheduleItem,
						backgroundColor: EVENT_PRESET[type].eventColor,
						borderColor: EVENT_PRESET[type].eventColor,
						textColor: '#000',
						editable: false,
						resourceId: serviceSchedule.workplaceId ?? undefined,
						allDay,
						repeatable: serviceScheduleItem.repeatable ?? false,
						workers: prepareWorkers(
							(serviceScheduleItem?.serviceScheduleItemMembers ||
								[]) as ServiceScheduleItemMember[],
						),
						caseTypes: prepareCaseTypes(
							(serviceScheduleItem?.serviceScheduleItemCaseTypes ||
								[]) as ServiceScheduleItemCaseType[],
						),
					};

					const occurrences = serviceScheduleItem?.occurrences;

					if (Array.isArray(occurrences) && occurrences?.length > 0) {
						serviceScheduleItem?.occurrences?.forEach(occurrence => {
							const timeFrom = prepareDateTime(
								serviceSchedule.dateFrom,
								serviceScheduleItem.timeFrom,
								occurrence,
								serviceScheduleItem.timeFrom,
							);
							const timeTo = prepareDateTime(
								serviceSchedule.dateFrom,
								serviceScheduleItem.timeFrom,
								occurrence,
								serviceScheduleItem.timeTo,
							);
							var duration = moment.duration(
								getMomentDate(timeTo).diff(getMomentDate(timeFrom)),
							);

							tempEvents.push({
								...baseEventData,
								start: moment.utc(timeFrom).format(),
								end: moment
									.utc(timeFrom)
									.add(duration.asSeconds(), 'seconds')
									.format(),
								isOccurrence: true,
								shift: {
									type: serviceSchedule.type ?? undefined,
									timeInterval: `${timeFrom.format('HH:mm')} - ${timeTo.format(
										'HH:mm',
									)}`,
									label: `${t(EVENT_PRESET[type].label)}: ${timeFrom.format(
										'HH:mm',
									)} - ${timeTo.format('HH:mm')}`,
								},
							});
						});
					}
				}
			});
		});

		setIsLoading(false);
		return tempEvents;
	};

	return {
		getWorkplaceSchedules,
		isLoading,
	};
}
